import {Dispatch, SetStateAction, useEffect, useMemo, useState} from 'react'
import {selectTheme} from '../../store'
import {PopupSize, PopupTemplate} from './popup_template'
import {ButtonReverse, QuestionnaireInput, QuestionnaireInputHeight, QuestionnaireInputSize, Select, Toggle} from '..'
import {AddIcon} from '../../lib/svg/icon/add'
import {
  IQuestionnaireDisplayLogicType,
  IQuestionnaireDisplayRuleSourceQuestion,
  IQuestionnaireDisplayRuleConditionOption,
  IQuestionnaireDisplayRuleConditionNumber,
  IQuestionnaireDisplayRuleTargetSection,
  IQuestionnaireDisplayRuleTargetQuestion,
  IQuestionnaireDisplayRuleTargetSubmit,
  QuestionnaireQuestionType,
  IQuestionnaireDisplayRuleConditionInputNumberOperator,
  IQuestionnaireDisplayRuleConditionType,
  IQuestionnaireDisplayRuleTargetType,
  IQuestionnaireDisplayRuleSourceType,
  IQuestionnaireDisplayRuleConditionOperator,
} from '../../shared/db'
import {
  QuestionnaireDisplayStrategyUpsertItem,
  QuestionnaireTempQuestion,
  QuestionnaireTempSection,
  TempQuestionnaireDisplayStrategyUpsertItem,
  UpsertDisplayRule,
} from '../../model'
import {DeleteIcon} from '../../lib/svg/icon/delete'
import {v4} from 'uuid'
import {useDebounce} from 'react-use'
import {SkipIcon} from '../../lib/svg/icon/skip'
import {InvisibleIcon} from '../../lib/svg/icon/invisible'
import {components, OptionProps} from 'react-select'
import {WarningIcon} from '../../lib/svg/icon/warning'

interface Option {
  value: string
  label: string
  isDisabled?: boolean
  icon?: React.ReactNode
  indent?: boolean
}

export const PopupEditQuestionnaireDisplayLogic = (props: {
  setRenderPopupEditDisplayLogic: Dispatch<SetStateAction<boolean>>
  targetType: 'question' | 'section'
  itemId: string
  upsertDisplayStrategyList: TempQuestionnaireDisplayStrategyUpsertItem[]
  setUpsertDisplayStrategyList: Dispatch<SetStateAction<TempQuestionnaireDisplayStrategyUpsertItem[]>>
  tempSectionList: QuestionnaireTempSection[]
}) => {
  const {color, fontSize, fontWeight} = selectTheme()
  const {
    setRenderPopupEditDisplayLogic,
    targetType,
    itemId,
    upsertDisplayStrategyList,
    setUpsertDisplayStrategyList,
    tempSectionList,
  } = props

  const questionMap = useMemo(() => {
    const map = new Map<string, QuestionnaireTempQuestion>()
    for (const section of tempSectionList) {
      for (const question of section.questionList ?? []) {
        map.set(question.id, question)
      }
    }
    return map
  }, [tempSectionList])

  // a list that contains all sections and questions in order
  const contentList = useMemo(() => {
    return tempSectionList.flatMap((section, index) => [
      {
        type: 'section',
        sectionId: section.id,
        id: section.id,
        title: section.legend || 'Untitled Section',
        index: index + 1,
        questionType: null,
        inputConfig: null,
      },
      ...(section.questionList?.map(({id, description, type, inputConfig}, i) => ({
        type: 'question',
        sectionId: section.id,
        id: id,
        title: description,
        index: i + 1,
        questionType: type,
        inputConfig,
      })) ?? []),
    ])
  }, [tempSectionList])
  const currentItemIndex = contentList.findIndex(({id}) => id === itemId)
  const currentItem = contentList[currentItemIndex]
  const currentQuestion = questionMap.get(currentItem?.id)
  // options for section skip logic
  const afterThisSectionOptions: Option[] = useMemo(() => {
    return [
      {
        value: 'next',
        label: '<Continue to next section>',
      },
      ...tempSectionList.map((section: QuestionnaireTempSection, index: number) => ({
        value: section.id,
        label: `Section ${index + 1}. ${section.legend || 'Untitled'}`,
        isDisabled: section.id === itemId,
      })),
      {
        value: 'submit',
        label: '<Submit the questionnaire>',
      },
    ]
  }, [tempSectionList])

  // check if the target section/question has display logic
  const hasDisplayLogic = (id: string, type: string): boolean => {
    return upsertDisplayStrategyList.some((item) => {
      if (type === 'section') {
        return item.sectionId === id
      } else if (type === 'question') {
        return item.questionId === id
      }
      return false
    })
  }

  // options for the question field in display rule
  const displayRuleQuestionOptions: Option[] = useMemo(() => {
    return contentList.map(({type, id, title, index, questionType}, i) => {
      const isDisabled =
        i >= currentItemIndex ||
        type === 'section' ||
        ![
          QuestionnaireQuestionType.Radio,
          QuestionnaireQuestionType.MultiSelect,
          QuestionnaireQuestionType.Number,
        ].includes(questionType as QuestionnaireQuestionType)
      return {
        value: id,
        label: `${type === 'section' ? `Section ${index}.` : `Q${index}.`} ${title}`,
        isDisabled,
        icon: hasDisplayLogic(id, 'question') ? (
          <InvisibleIcon width={16} height={16} fill={isDisabled ? color.grey_160 : color.textIcon.secondary} />
        ) : undefined,
        indent: type === 'question',
      }
    })
  }, [itemId])

  // options for the condition field in display rule
  const conditionOperatorOptions: Option[] = [
    {
      value: IQuestionnaireDisplayRuleConditionInputNumberOperator.Equal,
      label: 'equal to',
    },
    {
      value: IQuestionnaireDisplayRuleConditionInputNumberOperator.GreaterThan,
      label: 'more than',
    },
    {
      value: IQuestionnaireDisplayRuleConditionInputNumberOperator.GreaterThanOrEqual,
      label: 'more than or equal to',
    },
    {
      value: IQuestionnaireDisplayRuleConditionInputNumberOperator.LessThan,
      label: 'less than',
    },
    {
      value: IQuestionnaireDisplayRuleConditionInputNumberOperator.LessThanOrEqual,
      label: 'less than or equal to',
    },
    {
      value: 'between',
      label: 'between (inclusive)',
    },
  ]

  const CustomOption = (props: OptionProps<Option>) => {
    const {data, isDisabled, children} = props
    const textColor = isDisabled ? color.grey_160 : color.black
    const basePadding = 12
    const indentSize = 16
    const paddingLeft = data.indent ? basePadding + indentSize : basePadding
    return (
      <components.Option {...props}>
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            paddingLeft: `${paddingLeft}px`,
            color: textColor,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
          }}>
          {data.icon && data.icon}
          <span
            css={{
              color: textColor,
            }}>
            {children}
          </span>
        </div>
      </components.Option>
    )
  }

  const currentDisplayStrategy = useMemo(() => {
    return upsertDisplayStrategyList.find((item) => {
      if (targetType === 'section') {
        return item.sectionId === itemId
      } else if (targetType === 'question') {
        return item.questionId === itemId
      }
      return false
    })
  }, [itemId, upsertDisplayStrategyList])

  // previousQuestion is the closest previous question that can be a source of display rule
  // A valid previous question is one that:
  // 1. Comes before the current item in the same section, OR
  // 2. Belongs to a section that appears before the current section
  // Since contentList is ordered by section sequence, iterating backwards covers both cases
  const previousQuestion: QuestionnaireTempQuestion | null = useMemo(() => {
    if (currentItemIndex <= 0) return null

    const validSourceTypes = new Set([QuestionnaireQuestionType.Radio, QuestionnaireQuestionType.Number])

    for (let i = currentItemIndex - 1; i >= 0; i--) {
      const item = contentList[i]
      if (item.type === 'question') {
        if (validSourceTypes.has(item.questionType as QuestionnaireQuestionType)) {
          return questionMap.get(item.id) ?? null
        }
      }
    }
    return null
  }, [contentList, currentItemIndex, questionMap])

  const [enabledDisplayLogic, setEnabledDisplayLogic] = useState<boolean>(
    (currentDisplayStrategy?.displayStrategy?.displayLogicList?.some(
      ({type, displayRuleList}) =>
        type === IQuestionnaireDisplayLogicType.Display && displayRuleList && displayRuleList.length > 0,
    ) ??
      false) &&
      previousQuestion !== null,
  )
  const [enabledQuestionSkipLogic, setEnabledQuestionSkipLogic] = useState<boolean>(
    currentDisplayStrategy?.displayStrategy?.displayLogicList?.some(
      ({type, displayRuleList}) =>
        type === IQuestionnaireDisplayLogicType.Skip && displayRuleList && displayRuleList.length > 0,
    ) ?? false,
  )

  const [afterThisSection, setAfterThisSection] = useState<{value: string; label: string}>({
    value: 'next',
    label: '<Continue to next section>',
  })

  // displayRules and skipRules state
  const [displayRules, setDisplayRules] = useState<UpsertDisplayRule[]>([])
  const [skipRules, setSkipRules] = useState<UpsertDisplayRule[]>([])

  const defaultDisplayRuleCondition =
    previousQuestion?.type === QuestionnaireQuestionType.Number
      ? {
          type: IQuestionnaireDisplayRuleConditionType.Number,
          inputNumberList: [
            {
              operator: IQuestionnaireDisplayRuleConditionInputNumberOperator.Equal,
              value: 0,
            },
          ],
          operator: IQuestionnaireDisplayRuleConditionOperator.And,
        }
      : {
          type: IQuestionnaireDisplayRuleConditionType.Option,
          inputOptionList: [
            {
              optionId: previousQuestion?.inputConfig?.options?.[0]?.value ?? '',
            },
          ],
          operator: IQuestionnaireDisplayRuleConditionOperator.And,
        }

  const defaultDisplayRule = {
    source: {
      type: IQuestionnaireDisplayRuleSourceType.Question,
      questionnaireQuestionId: previousQuestion?.id ?? '',
    },
    condition: {...defaultDisplayRuleCondition},
  }

  const defaultSkipRuleForNumberTypeQuestion = useMemo(() => {
    if (!currentQuestion) return null
    const questionIndex = contentList.findIndex(({id}) => id === currentQuestion.id)
    const nextQuestion = contentList.slice(questionIndex + 1).find(({type}) => type === 'question')
    if (!nextQuestion) return null
    return {
      condition: {
        type: IQuestionnaireDisplayRuleConditionType.Number,
        inputNumberList: [
          {
            operator: IQuestionnaireDisplayRuleConditionInputNumberOperator.Equal,
            value: 0,
          },
        ],
        operator: IQuestionnaireDisplayRuleConditionOperator.And,
      },
      target: {
        type: IQuestionnaireDisplayRuleTargetType.Question,
        questionnaireQuestionId: nextQuestion.id,
      },
    }
  }, [contentList, itemId])

  const defaultSkipRulesForOptionTypeQuestion =
    currentQuestion?.inputConfig?.options?.map((option) => ({
      condition: {
        type: IQuestionnaireDisplayRuleConditionType.Option,
        inputOptionList: [
          {
            optionId: option.value,
          },
        ],
        operator: IQuestionnaireDisplayRuleConditionOperator.And,
      },
      target: {
        type: IQuestionnaireDisplayRuleTargetType.Question,
        questionnaireQuestionId: 'next',
      },
    })) ?? []

  const defaultSkipRuleForSection = {
    target: {
      type: IQuestionnaireDisplayRuleTargetType.Section,
    },
  }

  // Returns true if the input rule has conflicts with any of the other rules, false otherwise
  const hasSkipRuleConflict = (rule: UpsertDisplayRule, otherRules: UpsertDisplayRule[]): boolean => {
    if (otherRules.length === 0) return false

    // Helper function to compare targets
    const areTargetsEqual = (target1: UpsertDisplayRule['target'], target2: UpsertDisplayRule['target']): boolean => {
      if (!target1 || !target2) return false
      if (target1.type !== target2.type) return false
      if (target1.type === IQuestionnaireDisplayRuleTargetType.Submit) return true
      if (target1.type === IQuestionnaireDisplayRuleTargetType.Section) {
        return (
          (target1 as IQuestionnaireDisplayRuleTargetSection).questionnaireSectionId ===
          (target2 as IQuestionnaireDisplayRuleTargetSection).questionnaireSectionId
        )
      }
      if (target1.type === IQuestionnaireDisplayRuleTargetType.Question) {
        return (
          (target1 as IQuestionnaireDisplayRuleTargetQuestion).questionnaireQuestionId ===
          (target2 as IQuestionnaireDisplayRuleTargetQuestion).questionnaireQuestionId
        )
      }
      return false
    }

    // Helper function to check if two number conditions overlap
    const doNumberConditionsOverlap = (
      condition1: IQuestionnaireDisplayRuleConditionNumber,
      condition2: IQuestionnaireDisplayRuleConditionNumber,
    ): boolean => {
      // Get the range for each condition
      const getRange = (condition: IQuestionnaireDisplayRuleConditionNumber): {min: number; max: number} => {
        const inputNumberList = condition.inputNumberList ?? []
        if (inputNumberList.length === 0) return {min: -Infinity, max: Infinity}

        if (inputNumberList.length === 2) {
          // Between condition
          const values = inputNumberList.map((item) => item.value)
          return {min: Math.min(...values), max: Math.max(...values)}
        }

        const {operator, value} = inputNumberList[0]
        switch (operator) {
          case IQuestionnaireDisplayRuleConditionInputNumberOperator.Equal:
            return {min: value, max: value}
          case IQuestionnaireDisplayRuleConditionInputNumberOperator.GreaterThan:
            return {min: value + 0.0001, max: Infinity}
          case IQuestionnaireDisplayRuleConditionInputNumberOperator.GreaterThanOrEqual:
            return {min: value, max: Infinity}
          case IQuestionnaireDisplayRuleConditionInputNumberOperator.LessThan:
            return {min: -Infinity, max: value - 0.0001}
          case IQuestionnaireDisplayRuleConditionInputNumberOperator.LessThanOrEqual:
            return {min: -Infinity, max: value}
          default:
            return {min: -Infinity, max: Infinity}
        }
      }

      const range1 = getRange(condition1)
      const range2 = getRange(condition2)

      // Check if ranges overlap
      return range1.min <= range2.max && range2.min <= range1.max
    }

    const condition1 = rule.condition as
      | IQuestionnaireDisplayRuleConditionNumber
      | IQuestionnaireDisplayRuleConditionOption

    if (!condition1) return false

    // Check for conflicts between the input rule and all other rules
    for (const otherRule of otherRules) {
      // If targets are the same, no conflict
      if (areTargetsEqual(rule.target, otherRule.target)) continue

      const condition2 = otherRule.condition as
        | IQuestionnaireDisplayRuleConditionNumber
        | IQuestionnaireDisplayRuleConditionOption

      if (!condition2) continue

      // Check for option-based conflicts
      if (
        condition1.type === IQuestionnaireDisplayRuleConditionType.Option &&
        condition2.type === IQuestionnaireDisplayRuleConditionType.Option
      ) {
        const optionIds1 = new Set(condition1.inputOptionList?.map((item) => item.optionId) ?? [])
        const optionIds2 = condition2.inputOptionList?.map((item) => item.optionId) ?? []

        // Check if any option overlaps
        if (optionIds2.some((id) => optionIds1.has(id))) {
          return true // Conflict found: same option with different targets
        }
      }

      // Check for number-based conflicts
      if (
        condition1.type === IQuestionnaireDisplayRuleConditionType.Number &&
        condition2.type === IQuestionnaireDisplayRuleConditionType.Number
      ) {
        if (doNumberConditionsOverlap(condition1, condition2)) {
          return true // Conflict found: overlapping ranges with different targets
        }
      }
    }

    return false
  }

  const hasConflict = useMemo(() => {
    if (!enabledQuestionSkipLogic) return false
    for (let i = 0; i < skipRules.length; i++) {
      const rule = skipRules[i]
      const otherRules = skipRules.filter((_, index) => index !== i)
      if (hasSkipRuleConflict(rule, otherRules)) {
        return true
      }
    }
    return false
  }, [enabledQuestionSkipLogic, skipRules])

  // initialize displayRules and skipRules from upsertDisplayStrategyList
  // when component mounts or itemId or upsertDisplayStrategyList changes
  useMemo(() => {
    if (currentDisplayStrategy && currentDisplayStrategy.displayStrategy?.displayLogicList) {
      setDisplayRules(
        currentDisplayStrategy.displayStrategy.displayLogicList
          .filter((logic) => logic.type === IQuestionnaireDisplayLogicType.Display)
          .flatMap((logic) => logic.displayRuleList ?? []),
      )
      setSkipRules(
        currentDisplayStrategy.displayStrategy.displayLogicList
          .filter((logic) => logic.type === IQuestionnaireDisplayLogicType.Skip)
          .flatMap((logic) => logic.displayRuleList ?? []),
      )
    } else if (currentItem.type === 'section') {
      setSkipRules([{...defaultSkipRuleForSection}])
    }
  }, [itemId, upsertDisplayStrategyList])

  // initialize default skip rules for option type question
  useEffect(() => {
    if (!currentQuestion?.type) return
    if ([QuestionnaireQuestionType.Radio].includes(currentQuestion.type)) {
      if (skipRules.length === 0) {
        setSkipRules([...defaultSkipRulesForOptionTypeQuestion])
      } else {
        setSkipRules(
          [
            ...skipRules,
            ...defaultSkipRulesForOptionTypeQuestion.filter(({condition}) => {
              return !skipRules.some((rule) => {
                return (
                  (rule.condition as IQuestionnaireDisplayRuleConditionOption).inputOptionList[0].optionId ===
                  condition.inputOptionList[0].optionId
                )
              })
            }),
          ].sort((a, b) => {
            const aIndex =
              currentQuestion.inputConfig?.options?.findIndex(
                (option) =>
                  option.value ===
                  (a.condition as IQuestionnaireDisplayRuleConditionOption).inputOptionList[0].optionId,
              ) ?? -1
            const bIndex =
              currentQuestion.inputConfig?.options?.findIndex(
                (option) =>
                  option.value ===
                  (b.condition as IQuestionnaireDisplayRuleConditionOption).inputOptionList[0].optionId,
              ) ?? -1
            return aIndex - bIndex
          }),
        )
      }
    }
  }, [enabledQuestionSkipLogic])

  const handleSaveDisplayLogic = () => {
    const newDisplayStrategyList = [...upsertDisplayStrategyList]
    switch (targetType) {
      case 'section':
        {
          const itemIndex = newDisplayStrategyList.findIndex((item) => item.sectionId === itemId)
          if (!enabledDisplayLogic && afterThisSection.value === 'next') {
            newDisplayStrategyList.splice(itemIndex, 1)
            setUpsertDisplayStrategyList(newDisplayStrategyList)
          } else {
            const newDisplayStrategyItem: QuestionnaireDisplayStrategyUpsertItem = {
              sectionId: itemId,
              displayStrategy: {
                displayLogicList: [],
              },
            }
            if (afterThisSection.value !== 'next') {
              newDisplayStrategyItem.displayStrategy.displayLogicList?.push(
                afterThisSection.value === 'submit'
                  ? {
                      type: IQuestionnaireDisplayLogicType.Skip,
                      displayRuleList: [
                        {
                          target: {
                            type: IQuestionnaireDisplayRuleTargetType.Submit,
                          },
                        },
                      ],
                    }
                  : {
                      type: IQuestionnaireDisplayLogicType.Skip,
                      displayRuleList: [
                        {
                          target: {
                            type: IQuestionnaireDisplayRuleTargetType.Section,
                            questionnaireSectionId: afterThisSection.value,
                          },
                        },
                      ],
                    },
              )
            }
            if (enabledDisplayLogic) {
              newDisplayStrategyItem.displayStrategy.displayLogicList?.push({
                type: IQuestionnaireDisplayLogicType.Display,
                displayRuleList: displayRules,
              })
            }

            if (itemIndex === -1) {
              setUpsertDisplayStrategyList([...newDisplayStrategyList, newDisplayStrategyItem])
            } else {
              newDisplayStrategyList.splice(itemIndex, 1, newDisplayStrategyItem)
              setUpsertDisplayStrategyList(newDisplayStrategyList)
            }
          }
        }
        break
      case 'question': {
        const itemIndex = newDisplayStrategyList.findIndex((item) => item.questionId === itemId)
        if (!enabledQuestionSkipLogic && !enabledDisplayLogic) {
          newDisplayStrategyList.splice(itemIndex, 1)
          setUpsertDisplayStrategyList(newDisplayStrategyList)
        } else {
          const newDisplayStrategyItem: QuestionnaireDisplayStrategyUpsertItem = {
            questionId: itemId,
            displayStrategy: {
              displayLogicList: [],
            },
          }
          if (enabledDisplayLogic) {
            newDisplayStrategyItem.displayStrategy.displayLogicList?.push({
              type: IQuestionnaireDisplayLogicType.Display,
              displayRuleList: displayRules,
            })
          }
          if (enabledQuestionSkipLogic) {
            newDisplayStrategyItem.displayStrategy.displayLogicList?.push({
              type: IQuestionnaireDisplayLogicType.Skip,
              displayRuleList: skipRules,
            })
          }
          setTimeout(() => {
            if (itemIndex === -1) {
              setUpsertDisplayStrategyList([...newDisplayStrategyList, newDisplayStrategyItem])
            } else {
              newDisplayStrategyList.splice(itemIndex, 1, newDisplayStrategyItem)
              setUpsertDisplayStrategyList(newDisplayStrategyList)
            }
          }, 500)
        }
        break
      }
      default:
        break
    }
    setRenderPopupEditDisplayLogic(false)
  }

  const handleAddDisplayRule = () => {
    setDisplayRules([...displayRules, {...defaultDisplayRule}])
  }

  const handleAddSkipRule = () => {
    setSkipRules([...skipRules, {...defaultSkipRuleForNumberTypeQuestion}])
  }

  const handleRemoveDisplayRule = (index: number) => {
    const newDisplayRules = [...displayRules]
    newDisplayRules.splice(index, 1)
    setDisplayRules(newDisplayRules)
  }

  const handleRemoveSkipRule = (index: number) => {
    const newSkipRules = [...skipRules]
    newSkipRules.splice(index, 1)
    setSkipRules(newSkipRules)
  }

  const DisplayRuleEntry = (props: {rule: UpsertDisplayRule; index: number}) => {
    const {rule, index} = props
    const ruleSource = rule.source as IQuestionnaireDisplayRuleSourceQuestion
    const ruleCondition = rule.condition as
      | IQuestionnaireDisplayRuleConditionNumber
      | IQuestionnaireDisplayRuleConditionOption

    // selected option for source question in display rule
    const [sourceQuestion, setSourceQuestion] = useState<{value: string; label: string}>(
      displayRuleQuestionOptions.find(({value}) => value === ruleSource.questionnaireQuestionId) ??
        displayRuleQuestionOptions.filter(({isDisabled}) => !isDisabled)[0],
    )

    // options for question option selection in display rule
    const conditionTargetOptions: Option[] = useMemo(() => {
      return (
        contentList
          .find(({id}) => id === sourceQuestion.value)
          ?.inputConfig?.options?.map(({value, id, name}) => ({
            value: value ?? (id as string),
            label: name,
          })) ?? []
      )
    }, [sourceQuestion])

    // selected option and condition for display rule
    const [conditionTarget, setConditionTarget] = useState<{value: string; label: string}>(
      conditionTargetOptions.find(({value}) => value === ruleCondition.inputOptionList?.[0].optionId) ??
        conditionTargetOptions[0],
    )
    const [condition, setCondition] = useState<{value: string; label: string}>(
      ruleCondition.inputNumberList && ruleCondition.inputNumberList.length > 1
        ? {value: 'between', label: 'between (inclusive)'}
        : (conditionOperatorOptions.find(({value}) => value === ruleCondition.inputNumberList?.[0].operator) ??
            conditionOperatorOptions[0]),
    )

    // value for number condition in display rule
    const [conditionValue, setConditionValue] = useState<number>(ruleCondition.inputNumberList?.[0].value ?? 0)
    const [conditionValue2, setConditionValue2] = useState<number>(
      ruleCondition.inputNumberList && ruleCondition.inputNumberList.length > 1
        ? ruleCondition.inputNumberList[1].value
        : 0,
    )

    // update rule when any of the fields change and the new value is different from the previous one
    useDebounce(
      () => {
        const newSourceQuestion = contentList.find(({id}) => id === sourceQuestion.value)
        const newConditionType =
          newSourceQuestion?.questionType === QuestionnaireQuestionType.Number
            ? IQuestionnaireDisplayRuleConditionType.Number
            : IQuestionnaireDisplayRuleConditionType.Option
        const newCondition =
          newConditionType === IQuestionnaireDisplayRuleConditionType.Number
            ? {
                type: IQuestionnaireDisplayRuleConditionType.Number,
                inputNumberList:
                  condition.value === 'between'
                    ? [
                        {
                          operator:
                            conditionValue < conditionValue2
                              ? IQuestionnaireDisplayRuleConditionInputNumberOperator.GreaterThanOrEqual
                              : IQuestionnaireDisplayRuleConditionInputNumberOperator.LessThanOrEqual,
                          value: conditionValue,
                        },
                        {
                          operator:
                            conditionValue < conditionValue2
                              ? IQuestionnaireDisplayRuleConditionInputNumberOperator.LessThanOrEqual
                              : IQuestionnaireDisplayRuleConditionInputNumberOperator.GreaterThanOrEqual,
                          value: conditionValue2,
                        },
                      ]
                    : [
                        {
                          operator: condition.value as IQuestionnaireDisplayRuleConditionInputNumberOperator,
                          value: conditionValue,
                        },
                      ],
                operator: IQuestionnaireDisplayRuleConditionOperator.And,
              }
            : {
                type: IQuestionnaireDisplayRuleConditionType.Option,
                inputOptionList: [
                  {
                    optionId: conditionTarget?.value,
                  },
                ],
                operator: IQuestionnaireDisplayRuleConditionOperator.And,
              }
        const newRule = {
          source: {
            type: IQuestionnaireDisplayRuleSourceType.Question,
            questionnaireQuestionId: sourceQuestion.value,
          },
          condition: newCondition,
        }

        if (
          newRule.source.questionnaireQuestionId !== ruleSource.questionnaireQuestionId ||
          JSON.stringify(newRule) !== JSON.stringify(rule)
        ) {
          const newDisplayRules = [...displayRules]
          newDisplayRules.splice(index, 1, newRule)
          setDisplayRules(newDisplayRules)
        }
      },
      300,
      [sourceQuestion, conditionTarget, condition, conditionValue, conditionValue2],
    )

    return (
      <>
        {index > 0 && (
          <div css={{width: '100%', display: 'flex', alignItems: 'center'}}>
            <span
              css={{
                padding: '0 16px',
                color: color.textIcon.secondary,
              }}>
              Or
            </span>
            <div
              css={{
                width: '100%',
                height: 0,
                borderTop: `1px solid ${color.border.blueLight}`,
              }}
            />
          </div>
        )}
        <div
          css={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          <div
            css={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}>
            <div
              css={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}>
              <span
                css={{
                  color: color.textIcon.secondary,
                  whiteSpace: 'nowrap',
                }}>
                Display if
              </span>
              <Select
                css={{minHeight: '40px', width: '360px'}}
                value={sourceQuestion}
                onChange={setSourceQuestion}
                options={displayRuleQuestionOptions}
                components={{Option: CustomOption}}
              />

              {(rule.condition as IQuestionnaireDisplayRuleConditionNumber | IQuestionnaireDisplayRuleConditionOption)
                .type === IQuestionnaireDisplayRuleConditionType.Option ? (
                <>
                  <Select
                    css={{minHeight: '40px', width: '280px'}}
                    value={conditionTarget}
                    options={conditionTargetOptions}
                    onChange={setConditionTarget}
                  />
                  <span css={{color: color.textIcon.secondary, whiteSpace: 'nowrap'}}>is selected</span>
                </>
              ) : (
                <></>
              )}
            </div>
            {ruleCondition.type === IQuestionnaireDisplayRuleConditionType.Number && (
              <div
                css={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                }}>
                <span
                  css={{
                    color: color.textIcon.secondary,
                  }}>
                  is
                </span>
                <Select
                  css={{minHeight: '40px', width: '200px'}}
                  value={condition}
                  options={conditionOperatorOptions}
                  onChange={setCondition}
                />
                <QuestionnaireInput
                  {...{
                    placeholder: '0',
                    maxLength: 20,
                    inputType: 'number',
                    defaultValue: conditionValue.toString(),
                    onChangeAction: (value: string) => setConditionValue(Number(value)),
                    size: QuestionnaireInputSize.displayLogic,
                    questionnaireInputHeight: QuestionnaireInputHeight.medium,
                    hideWordCount: true,
                  }}
                />
                {condition.value === 'between' && (
                  <>
                    <span
                      css={{
                        color: color.textIcon.secondary,
                      }}>
                      and
                    </span>
                    <QuestionnaireInput
                      {...{
                        placeholder: '0',
                        maxLength: 20,
                        inputType: 'number',
                        defaultValue: conditionValue2.toString(),
                        onChangeAction: (value: string) => setConditionValue2(Number(value)),
                        size: QuestionnaireInputSize.displayLogic,
                        questionnaireInputHeight: QuestionnaireInputHeight.medium,
                        hideWordCount: true,
                      }}
                    />
                  </>
                )}
              </div>
            )}
          </div>
          <button
            onClick={() => handleRemoveDisplayRule(index)}
            css={{
              width: 32,
              height: 32,
              borderRadius: 4,
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ':hover': {
                backgroundColor: color.surface.hover.hoverGreyBg,
              },
            }}>
            <DeleteIcon
              {...{
                width: 24,
                height: 24,
                fill: color.textIcon.secondary,
              }}
            />
          </button>
        </div>
      </>
    )
  }

  const SkipRuleEntry = (props: {rule: UpsertDisplayRule; index: number; skipRules: UpsertDisplayRule[]}) => {
    const {rule, index, skipRules} = props
    const ruleTarget = rule.target as
      | IQuestionnaireDisplayRuleTargetSection
      | IQuestionnaireDisplayRuleTargetQuestion
      | IQuestionnaireDisplayRuleTargetSubmit
    const ruleCondition = rule.condition as
      | IQuestionnaireDisplayRuleConditionNumber
      | IQuestionnaireDisplayRuleConditionOption

    const skipRuleTargetOptions: Option[] = useMemo(() => {
      let sectionIndex = 0
      const defaultOptions = [
        ...contentList.map(({type, id, title, index}, i) => {
          if (type === 'section') {
            sectionIndex = index
          }
          const isDisabled = i <= currentItemIndex
          return {
            value: id,
            label: `${type === 'section' ? `Section ${index}.` : `S${sectionIndex}-Q${index}.`} ${title}`,
            isDisabled,
            icon: hasDisplayLogic(id, type) ? (
              <InvisibleIcon width={16} height={16} fill={isDisabled ? color.grey_160 : color.textIcon.secondary} />
            ) : undefined,
            indent: type === 'question',
          }
        }),
        {value: 'submit', label: '<Submit the questionnaire>'},
      ]
      if (currentItem.type === 'question' && currentQuestion?.type === QuestionnaireQuestionType.Number) {
        return defaultOptions
      } else {
        return [{value: 'next', label: '<Next Question>'}, ...defaultOptions]
      }
    }, [contentList])

    const [skipRuleCondition, setSkipRuleCondition] = useState<{value: string; label: string}>(
      ruleCondition.inputNumberList && ruleCondition.inputNumberList.length > 1
        ? {value: 'between', label: 'between (inclusive)'}
        : (conditionOperatorOptions.find(({value}) => value === ruleCondition.inputNumberList?.[0].operator) ??
            conditionOperatorOptions[0]),
    )

    const [skipRuleConditionValue, setSkipRuleConditionValue] = useState<number>(
      ruleCondition.inputNumberList?.[0].value ?? 0,
    )
    const [skipRuleConditionValue2, setSkipRuleConditionValue2] = useState<number>(
      ruleCondition.inputNumberList && ruleCondition.inputNumberList.length > 1
        ? (ruleCondition.inputNumberList?.[1].value ?? 0)
        : 0,
    )

    const [skipRuleTargetValue, setSkipRuleTargetValue] = useState<{value: string; label: string}>(
      ruleTarget.type === IQuestionnaireDisplayRuleTargetType.Submit
        ? {value: 'submit', label: '<Submit the questionnaire>'}
        : (skipRuleTargetOptions.find(
            ({value}) => value === ruleTarget.questionnaireQuestionId || value === ruleTarget.questionnaireSectionId,
          ) ?? skipRuleTargetOptions[0]),
    )

    const questionName = useMemo(() => {
      const name = currentQuestion?.inputConfig?.options?.[index].name
      if (!name || name.length === 0) {
        return 'Untitled Question'
      } else if (name.length > 50) {
        return name.slice(0, 25) + '...' + name.slice(-20)
      }
      return name
    }, [currentQuestion])

    useDebounce(
      () => {
        const newSkipRules = [...skipRules]
        const targetType = contentList.find(({id}) => id === skipRuleTargetValue.value)?.type
        const skipTarget =
          skipRuleTargetValue.value === 'submit'
            ? {
                type: IQuestionnaireDisplayRuleTargetType.Submit,
              }
            : targetType === 'section'
              ? {
                  type: IQuestionnaireDisplayRuleTargetType.Section,
                  questionnaireSectionId: skipRuleTargetValue.value,
                }
              : {
                  type: IQuestionnaireDisplayRuleTargetType.Question,
                  questionnaireQuestionId: skipRuleTargetValue.value,
                }
        if (currentQuestion?.type === QuestionnaireQuestionType.Number) {
          newSkipRules.splice(index, 1, {
            condition: {
              ...ruleCondition,
              inputNumberList:
                skipRuleCondition.value === 'between'
                  ? [
                      {
                        operator:
                          skipRuleConditionValue < skipRuleConditionValue2
                            ? IQuestionnaireDisplayRuleConditionInputNumberOperator.GreaterThanOrEqual
                            : IQuestionnaireDisplayRuleConditionInputNumberOperator.LessThanOrEqual,
                        value: skipRuleConditionValue,
                      },
                      {
                        operator:
                          skipRuleConditionValue < skipRuleConditionValue2
                            ? IQuestionnaireDisplayRuleConditionInputNumberOperator.LessThanOrEqual
                            : IQuestionnaireDisplayRuleConditionInputNumberOperator.GreaterThanOrEqual,
                        value: skipRuleConditionValue2,
                      },
                    ]
                  : [
                      {
                        operator: skipRuleCondition.value as IQuestionnaireDisplayRuleConditionInputNumberOperator,
                        value: skipRuleConditionValue,
                      },
                    ],
            },
            target: skipTarget,
          })
        } else {
          newSkipRules.splice(index, 1, {
            condition: {
              ...ruleCondition,
            },
            target: skipTarget,
          })
        }
        if (JSON.stringify(newSkipRules[index]) !== JSON.stringify(rule)) {
          setSkipRules(newSkipRules)
        }
      },
      300,
      [skipRuleCondition, skipRuleConditionValue, skipRuleTargetValue, skipRuleConditionValue2],
    )

    return (
      <div
        css={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}>
        {ruleCondition.type === IQuestionnaireDisplayRuleConditionType.Option && (
          <div
            css={{
              width: '100%',
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
            }}>
            <span css={{color: color.textIcon.secondary, whiteSpace: 'nowrap'}}>If answer is</span>
            <span css={{flex: 1, fontWeight: fontWeight.thick, whiteSpace: 'nowrap'}}>{questionName}</span>
            <span css={{color: color.textIcon.secondary, whiteSpace: 'nowrap'}}>then go to</span>
            <Select
              css={{minHeight: '40px', width: '360px'}}
              value={skipRuleTargetValue}
              onChange={setSkipRuleTargetValue}
              options={skipRuleTargetOptions}
              components={{Option: CustomOption}}
            />
          </div>
        )}
        {ruleCondition.type === IQuestionnaireDisplayRuleConditionType.Number && (
          <>
            {index > 0 && <div css={{width: '100%', height: 0, borderBottom: `1px solid ${color.border.default}`}} />}
            <div
              css={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
              <div css={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                  }}>
                  <span css={{color: color.textIcon.secondary}}>If answer is</span>
                  <Select
                    css={{minHeight: '40px', width: '200px'}}
                    value={skipRuleCondition}
                    onChange={setSkipRuleCondition}
                    options={conditionOperatorOptions}
                  />
                  <QuestionnaireInput
                    {...{
                      placeholder: '0',
                      maxLength: 20,
                      inputType: 'number',
                      defaultValue: skipRuleConditionValue.toString(),
                      onChangeAction: (value: string) => setSkipRuleConditionValue(Number(value)),
                      size: QuestionnaireInputSize.displayLogic,
                      questionnaireInputHeight: QuestionnaireInputHeight.medium,
                      hideWordCount: true,
                    }}
                  />
                  {skipRuleCondition.value === 'between' && (
                    <>
                      <span
                        css={{
                          color: color.textIcon.secondary,
                        }}>
                        and
                      </span>
                      <QuestionnaireInput
                        {...{
                          placeholder: '0',
                          maxLength: 20,
                          inputType: 'number',
                          defaultValue: skipRuleConditionValue2.toString(),
                          onChangeAction: (value: string) => setSkipRuleConditionValue2(Number(value)),
                          size: QuestionnaireInputSize.displayLogic,
                          questionnaireInputHeight: QuestionnaireInputHeight.medium,
                          hideWordCount: true,
                        }}
                      />
                    </>
                  )}
                </div>
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                  }}>
                  <span css={{color: color.textIcon.secondary}}>then go to</span>
                  <Select
                    css={{minHeight: '40px', width: '360px'}}
                    value={skipRuleTargetValue}
                    onChange={setSkipRuleTargetValue}
                    options={skipRuleTargetOptions}
                    components={{Option: CustomOption}}
                  />
                </div>
              </div>
              <button
                onClick={() => handleRemoveSkipRule(index)}
                css={{
                  width: 32,
                  height: 32,
                  borderRadius: 4,
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ':hover': {
                    backgroundColor: color.surface.hover.hoverGreyBg,
                  },
                }}>
                <DeleteIcon
                  {...{
                    width: 24,
                    height: 24,
                    fill: color.textIcon.secondary,
                  }}
                />
              </button>
            </div>
          </>
        )}
        {hasSkipRuleConflict(rule, skipRules) && (
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
            <WarningIcon />
            <span
              css={{
                fontWeight: fontWeight.bold,
                color: color.state.attention,
              }}>
              This rule conflicts with another setting. Please adjust before proceeding.
            </span>
          </div>
        )}
      </div>
    )
  }

  return (
    <PopupTemplate
      {...{
        title: 'Edit Logic',
        background: true,
        popupSize: PopupSize.Large,
        handleClosePopup: () => setRenderPopupEditDisplayLogic(false),
        mainFunction: handleSaveDisplayLogic,
        buttonContent: 'Save',
        buttonDisabled: hasConflict,
      }}>
      <div
        css={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          // padding: '0 24px',
        }}>
        {/* display logic */}
        <div
          css={{
            width: '100%',
            padding: '16px',
            borderRadius: '16px',
            backgroundColor: enabledDisplayLogic ? color.surface.blue.light : color.surface.default,
            display: 'flex',
            flexDirection: 'column',
            gap: enabledDisplayLogic ? '24px' : '0',
            transition: 'background-color 0.3s ease-in-out, gap 0.3s ease-in-out',
          }}>
          <div
            css={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            {/* left part */}
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}>
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                <InvisibleIcon width={20} />
                <span css={{fontSize: fontSize.h5, fontWeight: fontWeight.bold}}>Display Logic</span>
              </div>
              <span
                css={{
                  color: color.textIcon.secondary,
                }}>{`Show this ${targetType} only if certain conditions are met.`}</span>
            </div>
            {/* right part */}
            <div
              css={{
                display: 'flex',
                alignItems: 'flex-end',
                flexDirection: 'column',
                gap: '4px',
              }}>
              <Toggle
                {...{
                  enabled: enabledDisplayLogic,
                  onChange: setEnabledDisplayLogic,
                  disabled: previousQuestion === null,
                }}
              />
              {previousQuestion === null && (
                <span css={{color: color.textIcon.secondary}}>Display logic requires a previous question.</span>
              )}
            </div>
          </div>
          {enabledDisplayLogic && (
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
              }}>
              {/* display rule list */}
              <div
                css={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}>
                {displayRules.map((rule, index) => (
                  <DisplayRuleEntry
                    {...{
                      key: v4(),
                      rule,
                      index,
                    }}
                  />
                ))}
              </div>
              <ButtonReverse onClick={handleAddDisplayRule} btnPadding='mediumSmall'>
                <>
                  <AddIcon {...{width: 12, height: 12, fill: color.textIcon.link}} />
                  <span css={{fontWeight: fontWeight.thick, color: color.textIcon.link}}>Add Rule</span>
                </>
              </ButtonReverse>
            </div>
          )}
        </div>

        {/* section skip logic */}
        {targetType === 'section' && (
          <div
            css={{
              width: '100%',
              padding: '16px',
              borderRadius: '16px',
              backgroundColor: color.surface.default,
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}>
            <span css={{color: color.textIcon.secondary}}>After this section</span>
            <Select
              css={{minHeight: '40px', width: '360px'}}
              value={afterThisSection}
              onChange={setAfterThisSection}
              options={afterThisSectionOptions}
            />
          </div>
        )}

        {/* question skip logic */}
        {targetType === 'question' && (
          <div
            css={{
              width: '100%',
              padding: '16px',
              borderRadius: '16px',
              backgroundColor: enabledQuestionSkipLogic ? color.surface.blue.light : color.surface.default,
              display: 'flex',
              flexDirection: 'column',
              gap: enabledQuestionSkipLogic ? '24px' : '0',
              transition: 'background-color 0.3s ease-in-out, gap 0.3s ease-in-out',
            }}>
            {/* skip logic header */}
            <div
              css={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
              {/* left part */}
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}>
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                  <SkipIcon />
                  <span css={{fontSize: fontSize.h5, fontWeight: fontWeight.bold}}>Skip Logic</span>
                </div>
                <span css={{color: color.textIcon.secondary}}>
                  Jump to a specific question based on the participant's answer.
                  <br />
                  <br />
                  Note: We do not check if there are any missing possibilities, please check your work.
                </span>
              </div>
              {/* right part */}
              <div css={{display: 'flex', alignItems: 'flex-end', flexDirection: 'column', gap: '4px'}}>
                <Toggle
                  {...{
                    enabled: enabledQuestionSkipLogic,
                    onChange: setEnabledQuestionSkipLogic,
                    disabled: ![QuestionnaireQuestionType.Radio, QuestionnaireQuestionType.Number].includes(
                      currentQuestion?.type as QuestionnaireQuestionType,
                    ),
                  }}
                />
                {![QuestionnaireQuestionType.Radio, QuestionnaireQuestionType.Number].includes(
                  currentQuestion?.type as QuestionnaireQuestionType,
                ) && (
                  <span css={{color: color.textIcon.secondary, whiteSpace: 'nowrap'}}>
                    Only support Radio Buttons and Number type.
                  </span>
                )}
              </div>
            </div>
            {/* skip logic rule content */}
            {enabledQuestionSkipLogic && (
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '24px',
                }}>
                {/* skip rule list */}
                <div
                  css={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: currentQuestion?.type === QuestionnaireQuestionType.Number ? '24px' : '8px',
                  }}>
                  {skipRules.map((rule, index) => (
                    <SkipRuleEntry
                      {...{
                        key: v4(),
                        rule,
                        skipRules,
                        index,
                      }}
                    />
                  ))}
                </div>
                {currentQuestion?.type === QuestionnaireQuestionType.Number && (
                  <ButtonReverse onClick={handleAddSkipRule} btnPadding='mediumSmall'>
                    <>
                      <AddIcon {...{width: 12, height: 12, fill: color.textIcon.link}} />
                      <span css={{fontWeight: fontWeight.thick, color: color.textIcon.link}}>Add Rule</span>
                    </>
                  </ButtonReverse>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </PopupTemplate>
  )
}
